import { useEffect, useState } from 'react';
import { Button, Modal } from '@heroui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TemplateSelectionStep from '@components/templates/TemplateSelectionStep';
import TimerTypeSelectionStep from '@components/templates/TimerTypeSelectionStep';
import { CountdownPosition, TimerType } from '@/types';
import { getDefaultCountdownPosition } from '@lib/countdownPosition';
import type { TimerTemplateId } from '@lib/timerTemplates';

type WizardStep = 'timer-type' | 'template';

export type TimerCreationWizardSelectPayload = {
	templateId: TimerTemplateId;
	timerType: TimerType;
	countdownPosition?: CountdownPosition;
};

type TimerCreationWizardModalProps = {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onSelect: (payload: TimerCreationWizardSelectPayload) => void;
};

export default function TimerCreationWizardModal({ isOpen, onOpenChange, onSelect }: TimerCreationWizardModalProps) {
	const [step, setStep] = useState<WizardStep>('timer-type');
	const [timerType, setTimerType] = useState<TimerType>(TimerType.BAR);
	const [selectedTemplateId, setSelectedTemplateId] = useState<TimerTemplateId | null>(null);

	const resetWizard = () => {
		setStep('timer-type');
		setTimerType(TimerType.BAR);
		setSelectedTemplateId(null);
	};

	useEffect(() => {
		if (!isOpen) {
			resetWizard();
		}
	}, [isOpen]);

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			resetWizard();
		}

		onOpenChange(nextOpen);
	};

	const handleTimerTypeSelect = (selectedTimerType: TimerType) => {
		setTimerType(selectedTimerType);
		setSelectedTemplateId(null);
		setStep('template');
	};

	const handleConfirmTemplate = () => {
		if (!selectedTemplateId) {
			return;
		}

		onSelect({
			templateId: selectedTemplateId,
			timerType,
			countdownPosition: getDefaultCountdownPosition(timerType),
		});
	};

	return (
		<Modal>
			<Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange} variant='blur'>
				<Modal.Container size='cover' scroll='inside' className='max-h-[90vh]'>
					<Modal.Dialog>
						<Modal.Header className='flex flex-row items-center justify-between gap-4'>
							<Modal.Heading>
								{step === 'timer-type' ? 'Step 1: Choose timer type' : 'Step 2: Select template'}
							</Modal.Heading>
							<button
								type='button'
								aria-label='Close timer creation'
								className='rounded-full p-1 text-[#111827] transition hover:bg-[#F3F5F8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#116DFF]'
								onClick={() => handleOpenChange(false)}
							>
								<XMarkIcon className='h-8 w-8' />
							</button>
						</Modal.Header>
						<Modal.Body className='min-h-[58vh] border-y border-[#ECEFF3] p-4 lg:p-8'>
							{step === 'timer-type' ? (
								<TimerTypeSelectionStep onSelect={handleTimerTypeSelect} />
							) : (
								<TemplateSelectionStep
									timerType={timerType}
									countdownPosition={getDefaultCountdownPosition(timerType)}
									selectedTemplateId={selectedTemplateId}
									onSelect={setSelectedTemplateId}
								/>
							)}
						</Modal.Body>
						{step === 'template' && (
							<Modal.Footer className='flex items-center justify-between'>
								<Button variant='secondary' onPress={() => setStep('timer-type')}>
									Back
								</Button>
								<Button variant='primary' isDisabled={!selectedTemplateId} onPress={handleConfirmTemplate}>
									Select
								</Button>
							</Modal.Footer>
						)}
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
